import React from "react";
import { Link } from "wouter";
import { Layout } from "@/components/layout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { TrendingUp, DollarSign, Target, PieChart, BarChart3, Calendar } from "lucide-react";

export default function BudgetsPage() {
  return (
    <Layout>
      <div className="p-6 space-y-6">
      <div className="space-y-2">
        <h1 className="text-3xl font-bold tracking-tight">AI Budget Forecasting</h1>
        <p className="text-muted-foreground">
          Leverage AI-powered insights to create accurate budget forecasts and financial projections for your business.
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Revenue Forecast</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">$124,500</div>
            <p className="text-xs text-muted-foreground">
              +8.2% from last period
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Expense Budget</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">$89,200</div>
            <p className="text-xs text-muted-foreground">
              -3.1% from last period
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Net Profit</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">$35,300</div>
            <p className="text-xs text-muted-foreground">
              +12.4% from last period
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5" />
              Budget Analysis
            </CardTitle>
            <CardDescription>
              AI-powered budget analysis and recommendations
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Revenue vs Budget</span>
                <span className="text-green-600">+8.2%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-green-600 h-2 rounded-full" style={{ width: "108%" }}></div>
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Expenses vs Budget</span>
                <span className="text-blue-600">-3.1%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div className="bg-blue-600 h-2 rounded-full" style={{ width: "97%" }}></div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Forecasting Tools
            </CardTitle>
            <CardDescription>
              Access advanced AI forecasting capabilities
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Link href="/budgets/ai-forecasting">
              <Button className="w-full justify-start" variant="outline">
                <TrendingUp className="mr-2 h-4 w-4" />
                Advanced AI Forecasting
              </Button>
            </Link>
            <Button className="w-full justify-start" variant="outline">
              <PieChart className="mr-2 h-4 w-4" />
              Budget Scenarios
            </Button>
            <Button className="w-full justify-start" variant="outline">
              <BarChart3 className="mr-2 h-4 w-4" />
              Variance Analysis
            </Button>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>AI Insights & Recommendations</CardTitle>
          <CardDescription>
            Smart recommendations based on your financial data and market trends
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="border-l-4 border-green-500 pl-4">
              <h4 className="font-semibold text-green-700">Revenue Opportunity</h4>
              <p className="text-sm text-gray-600">
                Based on seasonal trends, consider increasing marketing spend by 15% in Q2 to capture 
                the spring sales surge. Historical data shows this typically yields 2.3x ROI.
              </p>
            </div>
            <div className="border-l-4 border-blue-500 pl-4">
              <h4 className="font-semibold text-blue-700">Cost Optimization</h4>
              <p className="text-sm text-gray-600">
                Your software expenses have increased 23% year-over-year. Consider consolidating 
                redundant tools to save approximately $2,400 annually.
              </p>
            </div>
            <div className="border-l-4 border-yellow-500 pl-4">
              <h4 className="font-semibold text-yellow-700">Cash Flow Alert</h4>
              <p className="text-sm text-gray-600">
                Projected cash flow dip in month 3. Consider securing a line of credit or 
                adjusting payment terms with key customers.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
      </div>
    </Layout>
  );
}